import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { requireAuth, useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useFiles, useFileCategories } from "hooks/crm/useFiles";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import { labelFor } from "utilities/fileCategories";
import SearchPanel from "components/SearchPanel";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import RecordTable from "components/Records/RecordTable";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import FilePreview from "components/FilePreview";
import ConfirmDialog from "components/ConfirmDialog";
import RelativeTime from "components/RelativeTime";
import EmptyState from "components/EmptyState";
import { toCsv, csvFilename, downloadCsv } from "utilities/csv";
import {
  Filter,
  Download,
  Eye,
  FileText,
  FolderOpen,
  HardDrive,
  Users,
  Pencil,
  Trash2,
  Check,
  X,
} from "lucide-react";

const FIELDS = filterFieldsFor("files");

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPreviewable(mimeType) {
  return mimeType?.startsWith("image/") || mimeType === "application/pdf";
}

function FilesInner() {
  const navigate = useNavigate();
  const { filters, set } = useSearch();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  // `scope: "all"` is what makes this a CROSS-COMPANY list rather than a second
  // copy of the Files tab. The server honours it for internal callers only, so
  // this is a request rather than a grant — and the route below is internal-only
  // anyway.
  const {
    files,
    pagination,
    summary,
    loading,
    error,
    refetch,
    downloadFile,
    fetchAllRows,
    getPreviewUrl,
    deleteFile,
    renameFile,
    moveFile,
  } = useFiles({ filters, page, limit, sort, scope: "all" });

  const { categories } = useFileCategories({ scope: "all" });
  // The move picker offers EVERY choice; the filter above offers only what
  // exists. Different questions, different lists.
  const { categories: allCategories } = useFileCategories({ scope: "all", all: true });

  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const activeFilterCount = countActiveFilters(filters, FIELDS);

  const handlePreview = async (file) => {
    const signedUrl = await getPreviewUrl(file.fileId);
    if (signedUrl) setPreviewFile({ ...file, signedUrl });
  };

  const handleNameClick = (file) => {
    if (isPreviewable(file.mimeType)) handlePreview(file);
    else downloadFile(file.fileId);
  };

  const startEdit = (file) => {
    setEditingId(file.fileId);
    setEditName(file.displayName || file.fileName);
    setEditCategory(file.category || "general");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditCategory("");
  };

  /**
   * Saves only what changed, and targets the FILE's company — not the page's.
   * Every mutation is scope-checked server-side against the file's own
   * companyId, and this list spans companies.
   */
  const submitEdit = async (file) => {
    const name = editName.trim();
    const nameChanged = name && name !== (file.displayName || file.fileName);
    const categoryChanged = editCategory && editCategory !== file.category;

    if (nameChanged) await renameFile(file.fileId, name, file.companyId);
    if (categoryChanged) await moveFile(file.fileId, editCategory, file.companyId);
    if (nameChanged || categoryChanged) await refetch();

    setEditingId(null);
  };

  const handleDelete = (file) => {
    setConfirmDialog({
      open: true,
      title: "Delete File",
      description: `Delete "${file.displayName || file.fileName}"${
        file.companyName ? ` from ${file.companyName}` : ""
      }? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        await deleteFile(file.fileId, file.companyId);
        await refetch();
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        key: "companyName",
        width: "20%",
        header: "Client",
        // No sortKey: companyName is resolved AFTER the paginated query, so
        // Mongo cannot order by it. Same rule as the Client column on /mids.
        exportValue: (f) => f.companyName || "",
        cell: (f) =>
          f.companyId ? (
            <button
              className="truncate text-left hover:text-brand transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/companies/${f.companyId}?tab=files`);
              }}
              title="Open this client's Files tab"
            >
              {f.companyName || "—"}
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "displayName",
        width: "30%",
        header: "Name",
        sortKey: "displayName",
        exportValue: (f) => f.displayName || f.fileName,
        cell: (f) =>
          editingId === f.fileId ? (
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitEdit(f);
                  if (e.key === "Escape") cancelEdit();
                }}
                onClick={(e) => e.stopPropagation()}
                className="input h-7 text-sm flex-1"
                autoFocus
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <button
                onClick={() => handleNameClick(f)}
                className="truncate text-left hover:text-brand transition-colors"
                title={isPreviewable(f.mimeType) ? "Preview" : "Download"}
              >
                {f.displayName || f.fileName}
              </button>
            </div>
          ),
      },
      {
        key: "category",
        width: "18%",
        header: "Category",
        sortKey: "category",
        // Exports the STORED value, not the label — an export is data, and a
        // label cannot be filtered or joined on.
        exportValue: (f) => f.category,
        cell: (f) =>
          editingId === f.fileId ? (
            <select
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="select h-7 text-xs w-full"
              aria-label="Category"
            >
              {allCategories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="badge badge-muted text-xs">
              {labelFor(categories, f.category)}
            </span>
          ),
      },
      {
        key: "size",
        width: "8%",
        header: "Size",
        sortKey: "size",
        exportValue: (f) => f.size,
        cell: (f) => <span className="text-muted-foreground">{formatBytes(f.size)}</span>,
      },
      {
        key: "createdAt",
        width: "12%",
        header: "Uploaded",
        sortKey: "createdAt",
        exportValue: (f) => f.createdAt,
        cell: (f) => <RelativeTime value={f.createdAt} />,
      },
      {
        key: "uploadedBy",
        width: "12%",
        header: "Uploaded By",
        // No sortKey: uploadedBy is an object, and most historical files carry
        // none — the registration path never stamped one until SOUND-661.
        exportValue: (f) => f.uploadedBy?.displayName || f.uploadedBy?.email || "",
        cell: (f) => (
          <span className="text-muted-foreground truncate">
            {f.uploadedBy?.displayName || f.uploadedBy?.email || "—"}
          </span>
        ),
      },
      {
        key: "actions",
        width: "10%",
        header: "",
        exportable: false,
        cell: (f) =>
          editingId === f.fileId ? (
            <div className="flex items-center justify-end gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  submitEdit(f);
                }}
                className="p-1.5 rounded hover:bg-muted transition-colors text-success"
                title="Save"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cancelEdit();
                }}
                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-1">
              {isPreviewable(f.mimeType) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(f);
                  }}
                  className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  title="Preview"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile(f.fileId);
                }}
                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit(f);
                }}
                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Edit name and category"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(f);
                }}
                className="p-1.5 rounded hover:bg-muted transition-colors text-destructive"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, allCategories, editingId, editName, editCategory, navigate]
  );

  /**
   * Exports every row matching the filters, NOT the visible page.
   *
   * Exporting `files` would silently hand over 25 of however many matched — the
   * same silent truncation this ticket removed from the repo layer and from
   * Download All. `fetchAllRows` omits page/limit, which the list contract
   * supports.
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await fetchAllRows();
      downloadCsv(csvFilename("files"), toCsv(rows, columns));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Files"
        subtitle="Every document across the portfolio."
        meta={[{ label: "Total", value: (pagination.total || 0).toLocaleString() }]}
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="badge badge-brand ml-1">{activeFilterCount}</span>
              )}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleExport}
              disabled={exporting || !files.length}
            >
              <Download className="h-4 w-4" />
              {exporting ? "Exporting…" : "Export"}
            </button>
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        <PageKpis
          items={[
            {
              label: "Files",
              value: pagination.total || 0,
              tone: "brand",
              loading,
              icon: <FolderOpen />,
            },
            {
              // Aggregated SERVER-side over the same filter as the page. Summing
              // the 25 visible rows and calling it a portfolio total is the bug
              // SOUND-295 removed — it looks like an answer and changes as you
              // page. Passed as a NUMBER: PageKpis runs Number(value) on every
              // branch, so a pre-formatted string renders NaN.
              label: "Total size",
              value: summary?.totalBytes || 0,
              format: "bytes",
              tone: "neutral",
              loading,
              icon: <HardDrive />,
            },
            {
              label: "Clients",
              value: summary?.companyCount || 0,
              tone: "neutral",
              loading,
              icon: <Users />,
            },
          ]}
        />

        <ListSearchBar
          value={filters.search || ""}
          onChange={(v) => set("search", v)}
          placeholder="Search file name"
        >
          <select
            className="select w-auto"
            value={filters.category || ""}
            onChange={(e) => set("category", e.target.value)}
            aria-label="Category"
          >
            <option value="">Category: Any</option>
            {categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </ListSearchBar>

        <RecordTable
          columns={columns}
          rows={files}
          rowKey={(f) => f.fileId}
          loading={loading}
          error={error}
          onRetry={refetch}
          filtered={activeFilterCount > 0 || Boolean(filters.search || filters.category)}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          noMatch={{ icon: FolderOpen, heading: "No matching files" }}
          empty={{
            icon: FolderOpen,
            heading: "No files yet",
            description: "Documents uploaded against any client will appear here.",
            errorText: "Failed to load files.",
          }}
        />

        <ListPagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 0}
          total={pagination.total || 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      {previewFile && (
        <FilePreview
          file={previewFile}
          signedUrl={previewFile.signedUrl}
          onClose={() => setPreviewFile(null)}
        />
      )}

      <ConfirmDialog
        {...confirmDialog}
        onCancel={() => setConfirmDialog((c) => ({ ...c, open: false }))}
      />

      <SearchPanel
        namespace="files"
        title="Filter Files"
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        extraOptions={{ categoryOptions: categories }}
      />
    </div>
  );
}

function Files() {
  const { user } = useAuth();

  // INTERNAL ONLY. This is the one surface that reads files across companies,
  // and the collection holds 175 driver's licences and 247 personal bank
  // statements. A client or partner already has the per-company Files tab —
  // the same data, correctly scoped — so a global list would add reach without
  // adding capability. The server enforces this too: `scope=all` is ignored for
  // non-internal callers, so a hand-typed URL gets their own company, not a leak.
  if (!isInternal(user)) {
    return (
      <EmptyState
        icon={FolderOpen}
        heading="Not available"
        description="Files are shown on each client's record."
      />
    );
  }

  return (
    <SearchProvider namespace="files">
      <FilesInner />
    </SearchProvider>
  );
}

export default requireAuth(Files);
